import React from 'react';
import { components, ValueContainerProps } from 'react-select';
import { OptionType } from '../../index.types';
import { cn } from '~/lib/utils';

/**
 * Custom value container for the Autocomplete.
 *
 * Extends the default container to display a secondary placeholder next to the
 * selected chips when the field is focused and no text has been typed yet.
 *
 * @returns A React element wrapping the selected chips, secondary placeholder, and input
 */
export const AutocompleteValueContainer = (
  props: ValueContainerProps<OptionType, true>
): React.ReactElement => {
  const { focusedPlaceholder, isSelectFocused } = props.selectProps as {
    focusedPlaceholder?: string;
    isSelectFocused?: boolean;
  };

  const inputValue = props.selectProps.inputValue ?? '';
  const hasValue = (props.selectProps.value as unknown[])?.length > 0;

  const showFocusedPlaceholder =
    !!focusedPlaceholder &&
    isSelectFocused &&
    inputValue.length === 0 &&
    hasValue;

  const children = React.Children.toArray(props.children);

  const inputChild = children[children.length - 1];
  const valueChildren = children.slice(0, children.length - 1);

  return (
    <components.ValueContainer {...props}>
      {valueChildren}
      {hasValue ? (
        <div className={cn('inline-flex items-center min-w-0', inputValue.length === 0 ? 'flex-none' : 'flex-1')}>
          {inputChild}
          {showFocusedPlaceholder && (
            <span className="text-muted-foreground text-sm pointer-events-none select-none whitespace-nowrap">
              {focusedPlaceholder}
            </span>
          )}
        </div>
      ) : (
        inputChild
      )}
    </components.ValueContainer>
  );
};